<script setup lang="ts">
import type { NavGroup, NavItem } from "@/definitions/navigation";
import type {
  NavigationGuard,
  RouteLocationNormalized,
  RouteLocationNormalizedLoaded,
} from "vue-router";
import { storeToRefs } from "pinia";
import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  useTemplateRef,
  watch,
} from "vue";
import { useRoute, useRouter } from "vue-router";
import AiAssistant from "@/components/AiAssistant.vue";
import Callout from "@/components/Callout.vue";
import IconButton from "@/components/IconButton.vue";
import LoadingButton from "@/components/LoadingButton.vue";
import {
  focusableIn,
  hasOpenModal,
  lockBodyScroll,
  trapTabKey,
} from "@/components/overlay";
import { provideRoleLowered } from "@/components/roleLowered";
import UiIcon from "@/components/UiIcon.vue";
import {
  getRelatedAccordionKeys,
  isNavGroup,
  navigation,
} from "@/definitions/navigation";
import { WORKSPACE_ROLE_LABELS } from "@/definitions/types";
import { useAuthStore } from "@/stores/auth";
import { hasPendingWrites } from "@/utils/apis";
import {
  applyWorkspaceSwitch,
  consumeLoweredWriteCancel,
  getTokenForWorkspace,
  logout,
} from "@/utils/commands";
import {
  dropLeaveGuards,
  hasUnsavedWork,
  registerEditedFieldProbe,
} from "@/utils/session";
import { toastError, toastInfo, toastSuccess } from "@/utils/toaster";

/**
 * 기본(로그인 후) 레이아웃
 * - 프로토타입 셸(`_shell_before_pages.html`)의 사이드바/상단바를 그대로 재현한다.
 * - 아코디언 열림 규칙은 `script_01.js` 의 `openRelatedAccordion` 과 동일하게 동작한다.
 *   (현재 페이지를 포함한 아코디언은 자동으로 열리고, 다른 아코디언은 닫지 않는다.)
 */

/** 프로토타입에서 최상위 그룹마다 붙어 있던 추가 클래스 */
const TOP_GROUP_CLASSES: Record<string, string> = {
  "quick-start": "quickstart-nav",
  "custom-design": "enterprise-design-nav accordion-design-nav",
};

const route = useRoute();
const router = useRouter();
const authStore = useAuthStore();
const { info, workspace, workspaces, pendingRole } = storeToRefs(authStore);

const currentPageId = computed(() => route.meta["pageId"] ?? "");
const currentTitle = computed(() => route.meta["title"] ?? "셀렉허브");

/**
 * 상단바 위치 줄에 쓰는 메뉴 그룹 경로(78차 UXA78-06 · VA78-26). 상단바가 「셀렉허브 / 미션 설계」 로 히어로 제목 · 사이드바 현재 메뉴와 같은 낱말을
 * 세 번째로 되풀이하던 것을, 현재 화면이 속한 그룹(「우리회사 맞춤 채용설계 / 조직적합성 설계」)으로 바꿔 **새 정보**(어느 단계 묶음인가)를 준다.
 * 86차 R86-visual-08: 직무분석 · 역량모델링 등은 h1 이 표어라 메뉴 이름이 화면 어디에도 없었다 — 경로 끝에 현재 화면 이름(`meta.title`)을
 * 한 칸 더 보인다(시안: 경로 끝 = 메뉴 이름). 상단바 `.page-title` 은 라우팅 계약으로 남기되 경로와 겹치므로 눈 · 낭독 모두에서 숨긴다.
 * 그룹을 보이는 최상위는 아래 셋 — 「시작」 은 화면 제목이 문장이라 겹치지 않고, 「새 채용시작」 은 단독 메뉴라 지금대로 「셀렉허브 / 제목」.
 */
const BREADCRUMB_ROOTS = ["custom-design", "operations", "management"];

function groupTrailOf(
  nodes: (NavGroup | NavItem)[],
  pageId: string,
  trail: string[],
): string[] | undefined {
  for (const node of nodes) {
    if (!isNavGroup(node)) {
      if (node.pageId === pageId) return trail;
      continue;
    }
    const found = groupTrailOf(node.children, pageId, [...trail, node.label]);
    if (found) return found;
  }
  return undefined;
}

const breadcrumbTrail = computed<string[]>(() => {
  const pageId = currentPageId.value;
  if (!pageId) return [];
  for (const root of navigation) {
    if (!BREADCRUMB_ROOTS.includes(root.key)) continue;
    const found = groupTrailOf(root.children, pageId, [root.label]);
    if (found) return found;
  }
  // 사이드바에 없는 하위 화면(리스크 상세)은 `meta.parentTitle` 한 칸이 경로다(89차 C89-06 · UXC89-10)
  const parent = route.meta["parentTitle"];
  return parent ? [parent] : [];
});

/** ≤620 에서 마지막 칸(화면 이름) 대신 가장 가까운 그룹 · 부모 칸을 보인다 — 히어로 제목과 같은 글을 되풀이하지 않게(89차 C89-06 · RG89-05) */
const showsNearGroup = computed(
  () =>
    breadcrumbTrail.value.length > 0 &&
    (route.meta["heroIsTitle"] === true || !!route.meta["parentTitle"]),
);

/**
 * 그룹 경로가 없는 시작 루트 화면이 h1 과 같은 이름이면(`meta.heroIsTitle` — `start/workspace`) ≤620 에서 상단바 화면 이름 대신 루트 「셀렉허브」 만 보인다
 * (90차 RG90-02 — 89차 C89-06 은 그룹 경로가 있는 화면만 덮었다). 화면 이름 칸 · `.page-title` 은 화면 읽기용으로 남긴다.
 */
const showsRootOnly = computed(
  () =>
    breadcrumbTrail.value.length === 0 && route.meta["heroIsTitle"] === true,
);

// 최초 진입 시 열어 둘 아코디언. 해당하는 그룹이 없으면 프로토타입 기본값(시작)을 연다.
const initialRelatedKeys = getRelatedAccordionKeys(route.meta["pageId"] ?? "");
const openKeys = ref<string[]>(
  initialRelatedKeys.length > 0 ? initialRelatedKeys : ["start-flow"],
);

const workspaceName = computed(
  () => workspace.value.name || "셀렉허브 워크스페이스",
);
const workspaceMeta = computed(() => {
  const plan = workspace.value.plan || "FREE";
  const role = workspace.value.role || info.value.role;
  const roleLabel = role ? WORKSPACE_ROLE_LABELS[role] : "구성원";
  // 73차 UXM-01: 셋째 값(현재 연도)은 뜻 없는 숫자가 요금제 · 역할과 같은 급으로 보여 뺐다(가입 연도는 워크스페이스 정보에 없다).
  return `${plan} · ${roleLabel}`;
});

function isOpen(key: string): boolean {
  return openKeys.value.includes(key);
}

function toggleAccordion(key: string): void {
  openKeys.value = isOpen(key)
    ? openKeys.value.filter((item) => item !== key)
    : [...openKeys.value, key];
}

function topGroupClass(group: NavGroup): string {
  return ["nav-group", TOP_GROUP_CLASSES[group.key], "accordion-group"]
    .filter(Boolean)
    .join(" ");
}

function isActive(item: NavItem): boolean {
  return item.pageId === currentPageId.value;
}

/** 하위 그룹은 메뉴 항목만 담는다(2단계 중첩까지). */
function leafItems(group: NavGroup): NavItem[] {
  return group.children.filter((child): child is NavItem => !isNavGroup(child));
}

/** 메뉴 항목 주소(`<a href>`) — 가운데 클릭 · 「새 탭에서 열기」 가 되도록 실제 주소를 건다(77차 UXS77-04) */
function navHref(path: string): string {
  return router.resolve(path).href;
}

/** 수정키 · 가운데 클릭은 브라우저 기본 동작(새 탭)에 맡기고, 그 밖의 누름만 라우터로 이동한다(이탈 가드는 `router.push` 가 그대로 탄다) */
function onNavClick(event: MouseEvent, path: string): void {
  if (
    event.defaultPrevented ||
    event.button !== 0 ||
    event.metaKey ||
    event.ctrlKey ||
    event.shiftKey ||
    event.altKey
  )
    return;
  event.preventDefault();
  void goTo(path);
}

async function goTo(path: string): Promise<void> {
  // 드로어에서 메뉴를 고르면 먼저 닫는다 — 이탈 확인창이 뜨면 드로어가 확인창 뒤에 남아 겹쳐 보인다. 포커스는 메뉴 버튼으로 돌린다 —
  // 같은 메뉴를 다시 고르거나 이탈 확인창을 취소하면 숨은 사이드바 버튼에 포커스가 남았다(리뷰 A-3).
  closeDrawer();
  if (route.path === path) return;
  await router.push(path);
}

/**
 * 워크스페이스 전환
 * - 소속 워크스페이스가 2개 이상일 때만 사이드바 카드에 셀렉트를 노출한다.
 * - **먼저 현재 화면을 떠나도 되는지 묻는다**(`confirmLeaveCurrentPage`). 전환은 라우트 이동이 아니라
 *   `RouterView` 의 key 로 페이지를 다시 마운트하므로, 묻지 않으면 저장하지 않은 편집이 확인 없이 사라지고
 *   진행 중인 쓰기(AI 생성·확정)도 아래 일괄 취소에 끊긴다.
 * - `GET api/v1/auth/refresh?workspaceId=` 로 해당 워크스페이스 스코프의 토큰을 재발급받는다.
 * - 쿼리 비우기 · 진행 중 요청 취소 · 토큰 · 스토어 갱신은 `applyWorkspaceSwitch`(`utils/commands.ts`)로
 *   올렸다 — 제거 뒤 강제 전환(`stores/auth.ts` `applyMe`, 3차 교차 연계 보완 Z3-01)과 같은 절차를 쓴다.
 *   이 화면은 그 앞뒤(이탈 확인 · 결과 토스트)만 맡는다.
 */
const switching = ref(false);
const workspaceSelect = useTemplateRef<HTMLSelectElement>("workspaceSelect");
const sidebarEl = useTemplateRef<HTMLElement>("sidebarEl");
const mainEl = useTemplateRef<HTMLElement>("mainEl");

/** 본문으로 건너뛰기(77차 UXS77-04) — 해시 이동은 주소를 바꾸므로 포커스만 옮긴다 */
function skipToMain(event: MouseEvent): void {
  event.preventDefault();
  mainEl.value?.focus();
}

/**
 * 화면 이동 알림(77차 UXS77-03) — 사이드바에서 메뉴를 눌러 화면이 바뀌어도 포커스는 눌렀던 자리에 남고 알리는 곳이 없어, 화면 읽기 사용자는
 * 화면이 바뀐 것을 몰랐다. 라이브 영역은 처음부터 DOM 에 두고(내용과 함께 나타나면 첫 알림을 놓친다) 경로(path)가 바뀔 때만 제목을 넣는다.
 * 쿼리만 바뀌는 이동(`?applicantId=`)은 알리지 않고, 포커스도 건드리지 않는다(저장 안 한 편집을 방해하지 않게).
 */
const routeAnnouncement = ref("");
watch(
  () => route.path,
  async () => {
    routeAnnouncement.value = "";
    await nextTick();
    routeAnnouncement.value = `${currentTitle.value} 화면`;
  },
);

/**
 * 가드 하나를 라우터와 같은 규칙으로 돌린다. 통과면 true.
 * 이 저장소의 가드는 모두 값을 돌려주지만, 폐기 예정인 `next` 콜백(인자 3개)을 쓰는 가드도 라우터처럼 받는다.
 */
function runLeaveGuard(
  guard: NavigationGuard,
  to: RouteLocationNormalized,
  from: RouteLocationNormalizedLoaded,
): Promise<boolean> {
  return new Promise((resolve) => {
    const allowed = (value: unknown) => value === undefined || value === true;
    const next = ((value?: unknown) => {
      resolve(allowed(value));
    }) as Parameters<NavigationGuard>[2];
    const result = guard(to, from, next);
    if (guard.length < 3) {
      Promise.resolve(result).then(
        (value) => {
          resolve(allowed(value));
        },
        () => {
          resolve(false);
        },
      );
    }
  });
}

/**
 * 현재 화면의 이탈 가드(`onBeforeRouteLeave`)를 직접 돌려 떠나도 되는지 묻는다.
 *
 * 라우터 이동으로는 가드를 부를 수 없다 — 같은 경로로 쿼리만 바꾸는 이동은 같은 라우트 레코드라
 * `onBeforeRouteLeave` 가 아니라 `onBeforeRouteUpdate` 만 부른다. 가드는 라우터가 쓰는 그대로
 * `RouteRecordNormalized.leaveGuards` 에 등록돼 있어(공개 타입) 화면마다 레지스트리를 따로 두지 않고
 * 기존 가드(직무분석 · 입사지원서 · 지원자관리 · 조직적합성 4종)를 모두 그대로 쓴다.
 * 라우터처럼 자식 레코드부터 돈다. 가드의 `to` 는 전환 뒤 도착할 같은 경로(쿼리 없음)다.
 */
async function confirmLeaveCurrentPage(): Promise<boolean> {
  const from = router.currentRoute.value;
  const to = router.resolve({ path: from.path }) as RouteLocationNormalized;
  for (const record of [...from.matched].reverse()) {
    for (const guard of [...record.leaveGuards]) {
      if (!(await runLeaveGuard(guard, to, from))) {
        return false;
      }
    }
  }
  return true;
}

/**
 * 로그아웃(30차 S30-02). 서버 세션을 폐기하면 되돌릴 수 없으므로 화면의 이탈 가드를 먼저 돌려 저장하지 않은 편집을 확인받는다 — 화면 가드는
 * `/login` 이동을 통과시켜 예전에는 묻지 않고 버렸다. 확인받았으면 가드를 떼고 보낸다(면접준비 · 면접 콘솔 가드는 `/login` 도 다시 묻는다).
 */
async function onLogout(): Promise<void> {
  if (!(await confirmLeaveCurrentPage())) return;
  dropLeaveGuards();
  await logout();
}

const selectedWorkspaceId = computed<number>({
  get: () => workspace.value.id,
  set: (id: number) => {
    void switchWorkspace(id);
  },
});

async function switchWorkspace(id: number): Promise<void> {
  if (switching.value || id === workspace.value.id) return;
  const target = workspaces.value.find((item) => item.id === id);
  if (!target) return;

  switching.value = true;
  try {
    if (!(await confirmLeaveCurrentPage())) {
      return;
    }
    const newTokens = await getTokenForWorkspace(id);
    if (!newTokens) {
      toastError("워크스페이스를 전환하지 못했습니다.");
      return;
    }

    if (!(await applyWorkspaceSwitch(target, newTokens))) {
      toastError("워크스페이스를 전환하지 못했습니다.");
      return;
    }
    toastSuccess(`${target.name} 워크스페이스로 전환했습니다.`);
  } finally {
    switching.value = false;
    // 취소·실패로 전환하지 않았으면 셀렉트가 고른 값으로 남아 있다(스토어 값이 그대로라 다시 그려지지 않는다).
    if (workspaceSelect.value) {
      workspaceSelect.value.value = String(workspace.value.id);
    }
  }
}

/**
 * 좁은 폭(1180px 이하) 내비게이션 드로어(ds-audit C1)
 * - 프로토타입은 이 폭에서 사이드바를 숨기고 페이지 이동 셀렉트(`.mobile-select`)만 두어, 워크스페이스 전환 · 로그아웃을 할 수 없었다.
 *   상단바 왼쪽 메뉴 버튼으로 사이드바 전체(워크스페이스 카드 · 메뉴 · 사용자 영역)를 왼쪽 드로어로 연다. 셀렉트는 중복이라 없앴다.
 * - 닫기: 닫기 버튼 · ESC · 배경막 클릭 · 메뉴 선택 · 라우트 변경 · 1180px 초과로 넓어짐. 닫으면 메뉴 버튼으로 포커스를 돌린다.
 * - 열려 있는 동안은 모달처럼 다룬다(role=dialog · aria-modal · Tab 순환 · body 스크롤 잠금). ESC 는 window 에서 받는다 — 드로어 위에
 *   확인창(AppModal)이 뜨면 확인창이 document 단계에서 ESC 를 멈춰 드로어까지 닫히지 않는다.
 */
const DRAWER_QUERY = "(max-width: 1180px)";
const drawerOpen = ref(false);
const menuButton = useTemplateRef<HTMLButtonElement>("menuButton");
let unlockDrawerScroll: (() => void) | undefined;
let drawerMedia: MediaQueryList | undefined;

function openDrawer(): void {
  if (drawerOpen.value) return;
  drawerOpen.value = true;
  unlockDrawerScroll = lockBodyScroll();
  void nextTick(() => {
    const sidebar = sidebarEl.value;
    if (!sidebar) return;
    // 현재 메뉴가 접힌 아코디언 안이면 보이지 않으므로 보이는 요소 중에서 고른다(리뷰 B5).
    const visible = focusableIn(sidebar);
    const target =
      visible.find((el) => el.matches(".nav-btn.active")) ?? visible[0];
    target?.focus({ preventScroll: true });
  });
}

function closeDrawer(restoreFocus = true): void {
  if (!drawerOpen.value) return;
  drawerOpen.value = false;
  unlockDrawerScroll?.();
  unlockDrawerScroll = undefined;
  if (restoreFocus) menuButton.value?.focus({ preventScroll: true });
}

function onDrawerKeydown(event: KeyboardEvent): void {
  // 드로어 위에 확인창(AppModal)이 떠 있으면 키보드는 확인창 몫이다. 여기서 Tab 을 가두면 포커스가 확인창 뒤 드로어로 빠졌다(리뷰 A-1).
  if (!drawerOpen.value || !sidebarEl.value || hasOpenModal()) return;
  // 한글 입력 조합 중 ESC 는 조합 취소라 드로어를 닫지 않는다(리뷰 B3).
  if (event.key === "Escape" && !event.isComposing) {
    closeDrawer();
    return;
  }
  trapTabKey(event, sidebarEl.value);
}

function onDrawerMediaChange(event: MediaQueryListEvent): void {
  if (!event.matches) closeDrawer(false);
}

/**
 * DEV: 본문 카드 안의 누를 수 없는 `ui-meta-chip` 검사(81차 ux-sys C11 ②). 32px 알약(`ui-meta-chip`)은 「누르는 칩 · 히어로 메타」 의 모양이라 본문 안 정보 칩으로 쓰면
 * 버튼처럼 보인다 — 정보 칩은 `UiTag`(md 24 · sm 20)다. 렌더는 그대로, 같은 글은 한 번만 경고한다.
 */
const warnedInfoChips = new Set<string>();
let infoChipObserver: MutationObserver | undefined;
let infoChipTimer: ReturnType<typeof setTimeout> | undefined;

function warnInfoChips(): void {
  const labels = [
    ...document.querySelectorAll<HTMLElement>(
      ".card .ui-meta-chip:not(.ui-tag, button, a, [role='button'], [role='tab'])",
    ),
  ]
    .filter((chip) => !chip.closest(".ui-hero, .ui-modal, [role='dialog']"))
    .map((chip) => chip.textContent.replace(/\s+/g, " ").trim())
    .filter((label) => label !== "" && !warnedInfoChips.has(label));
  if (labels.length === 0) return;
  for (const label of labels) warnedInfoChips.add(label);
  console.warn(
    `[ui-meta-chip] 카드 안 누를 수 없는 정보 칩 ${String(labels.length)}개 — UiTag 를 쓰세요(32px 알약은 버튼처럼 보입니다): ${labels
      .slice(0, 4)
      .map((label) => `「${label}」`)
      .join(" ")}`,
  );
}

function watchInfoChips(): void {
  infoChipObserver = new MutationObserver(() => {
    clearTimeout(infoChipTimer);
    infoChipTimer = setTimeout(warnInfoChips, 600);
  });
  infoChipObserver.observe(document.body, { childList: true, subtree: true });
}

let unregisterEditedFieldProbe: (() => void) | undefined;

onMounted(() => {
  document.addEventListener("input", markFieldEdited, true);
  // 세션 만료 · 제거 안내가 입력 중인 편집을 알아보게 한다(90차 AU90-06 — `hasUnsavedWork` 가 `beforeunload` 처리기 없는 화면의 편집도 본다).
  for (const type of ["pointerdown", "keydown", "input"] as const) {
    document.addEventListener(type, markInteracted, true);
  }
  unregisterEditedFieldProbe = registerEditedFieldProbe({
    edited: () => fieldEdited,
    interacted: () => interacted,
    editedFieldGone: () =>
      lastEditedField !== null && !lastEditedField.isConnected,
  });
  window.addEventListener("keydown", onDrawerKeydown);
  drawerMedia = window.matchMedia(DRAWER_QUERY);
  drawerMedia.addEventListener("change", onDrawerMediaChange);
  if (import.meta.env.DEV) watchInfoChips();
});

onBeforeUnmount(() => {
  document.removeEventListener("input", markFieldEdited, true);
  for (const type of ["pointerdown", "keydown", "input"] as const) {
    document.removeEventListener(type, markInteracted, true);
  }
  unregisterEditedFieldProbe?.();
  infoChipObserver?.disconnect();
  clearTimeout(infoChipTimer);
  window.removeEventListener("keydown", onDrawerKeydown);
  drawerMedia?.removeEventListener("change", onDrawerMediaChange);
  closeDrawer(false);
});

watch(
  () => route.fullPath,
  () => {
    closeDrawer(false);
  },
);

// 편집 · 손댐 표지는 **화면(경로)이 바뀔 때만** 비운다 — 역량 탭 · 직무분석 단계 · 현황 검색처럼 같은 화면 안에서 쿼리만 바뀌는 이동은 같은 화면의 같은 편집이다(N2).
watch(
  () => route.path,
  () => {
    fieldEdited = false;
    lastEditedField = null;
    interacted = false;
  },
);

/**
 * 역할이 바뀌면 화면을 다시 마운트해 새 역할로 다시 읽는다(4차 교차 연계 보완 W3-04 · 리뷰 M2).
 * 3차 Z3-01 이후 자동 재발급 · `must-renew` 로 화면을 떠나지 않고도 역할이 바뀐다. 가려서 받은 값(응답자 연락처 ·
 * 처우 등)을 든 채 쓰기가 열리면 저장이 가려진 빈 값으로 저장값을 덮으므로 다시 읽어야 한다.
 * - 다시 마운트하기 전에 워크스페이스 전환과 같은 `confirmLeaveCurrentPage` 로 화면의 이탈 가드를 돌린다 — key 재마운트는
 *   라우트 이동이 아니라 `onBeforeRouteLeave` 가 불리지 않아, 묻지 않으면 저장하지 않은 입력이 조용히 사라진다.
 * - 사용자가 미루면 상단 안내와 「화면 다시 불러오기」 버튼을 둔다. 그동안 올라간 역할은 스토어가 보류해(`pendingRole`)
 *   화면의 쓰기 게이트가 옛(낮은) 역할을 보고, 내려간 역할은 곧바로 반영돼 쓰기가 잠긴다.
 * - key 는 역할 대신 다시 불러온 횟수(`roleEpoch`)를 쓴다 — 역할이 처음 정해질 때(빈 값 → 역할)는 다시 마운트하지 않는다.
 * - 워크스페이스 전환 · 로그아웃은 id 가 바뀌어 어차피 다시 마운트되므로 묻지도 알리지도 않는다.
 * - **강등(역할이 내려감 · 보류 없이 바로 반영된 경우)은 자동으로 다시 마운트하지 않는다**(83차 JR83-05). 누른 동작 도중 재발급이 강등을 드러내면 자동 경로가 이탈 가드를 돌려
 *   「진행 중인 작업이 끝난 뒤 이동할 수 있습니다」 토스트와 「화면을 다시 불러옵니다」 토스트를 겹쳐 내고, 누른 동작은 결과 문장 없이 사라졌다. 이제 쓰기는 이미 잠겼으므로 머리 배너만 세우고
 *   (토스트 없음 — 취소된 쓰기의 문장은 요청 쪽이 한 번 낸다) 사용자가 입력을 옮겨 적은 뒤 「화면 다시 불러오기」 로 직접 다시 읽는다. 배너는 `#app` 로 Teleport 한 드로어 · 모달 뒤에 가려지므로
 *   그 층(`SidePanel` · `AppModal`)이 `provideRoleLowered` 문맥으로 같은 안내를 층 안 머리 아래에 한 번 더 그린다.
 */
// 새로 고침 직후에는 저장하지 않은 입력이 있을 수 없으므로, 영속 상태에 남은 보류 역할은 바로 반영한다
// (지금 토큰의 역할과 다르면 스토어가 버린다, 4차 재리뷰 N1).
authStore.applyPendingRole();
const mountedRole = ref(workspace.value.role);
const roleEpoch = ref(0);
const roleReloadBlocked = ref(false);
let roleReloading = false;
const desiredRole = computed(() => pendingRole.value || workspace.value.role);
const routerViewKey = computed(
  () => `${workspace.value.id}-${roleEpoch.value}-${route.path}`,
);

/**
 * `auto` 는 역할 변경을 감지한 자동 경로(8차 S3-01). 이탈 가드의 확인창은 "화면 이동" 용 문구라, 사용자가 누르지도 않은 이동 확인이
 * 까닭 없이 뜬다 — 그래서 확인창 앞에 역할이 바뀌어 다시 불러오려 한다는 안내를 먼저 띄우고, 완료 토스트는 겹치지 않게 생략한다.
 * 배너 버튼으로 직접 부를 때는 사용자가 이미 사정을 알므로 앞 안내 없이 완료 토스트만 띄운다.
 */
async function reloadForRole(auto = false): Promise<void> {
  if (roleReloading) return;
  roleReloading = true;
  try {
    if (auto) {
      toastInfo(
        "워크스페이스 역할이 바뀌어 화면을 다시 불러옵니다. 저장하지 않은 입력이 있어 확인창이 뜨면, 취소한 뒤 저장하고 상단 「화면 다시 불러오기」로 반영할 수 있습니다.",
      );
    }
    if (!(await confirmLeaveCurrentPage())) {
      roleReloadBlocked.value = desiredRole.value !== mountedRole.value;
      return;
    }
    authStore.applyPendingRole();
    mountedRole.value = workspace.value.role;
    roleReloadBlocked.value = false;
    roleEpoch.value += 1;
    fieldEdited = false;
    if (!auto) toastInfo("역할이 바뀌어 화면을 다시 불러왔습니다.");
  } finally {
    roleReloading = false;
  }
}

/** 지금 포커스가 쓸 수 있는 입력칸 안에 있다 */
function isEditingField(): boolean {
  const active = document.activeElement;
  if (!(active instanceof HTMLElement)) return false;
  if (active.isContentEditable) return true;
  if (
    active instanceof HTMLInputElement ||
    active instanceof HTMLTextAreaElement
  ) {
    return !active.readOnly && !active.disabled;
  }
  return active instanceof HTMLSelectElement && !active.disabled;
}

/** 이 화면에서 사용자가 입력칸 값을 고친 적이 있다(화면을 옮기면 비운다) — 처리기 없는 화면의 편집을 R2 가 알아보게 */
let fieldEdited = false;
/** 마지막으로 고친 입력칸 — 권한이 바뀌어 칸이 닫혔는지 본다(강등 취소 토스트 문구, RVFA90-02) */
let lastEditedField: HTMLElement | null = null;
/** 이 화면에 들어온 뒤 사용자가 손댔다(화면을 옮기면 비운다) — 열자마자 도는 로딩 · 요청 중 `beforeunload` 처리기를 편집으로 세지 않게(RVFA90-04) */
let interacted = false;
function markInteracted(): void {
  interacted = true;
}
function markFieldEdited(event: Event): void {
  const target = event.target;
  if (
    target instanceof HTMLInputElement ||
    target instanceof HTMLTextAreaElement ||
    target instanceof HTMLSelectElement
  ) {
    fieldEdited = true;
    lastEditedField = target;
  }
}

/** 강등을 드러낸 요청(재발급 중이던 쓰기)이 취소로 끝나 화면의 「저장 중」 이 풀리기를 기다리는 시간 */
const LOWERED_SETTLE_MS = 400;

/**
 * 강등 뒤 처리(83차 JR83-05 + 89차 R2). 기다린 뒤에도 아직 강등 상태이고 저장하지 않은 입력(`hasUnsavedWork`)도 진행 중 쓰기도 없으면
 * 자동으로 다시 불러온다 — 이탈 가드 토스트가 겹칠 일이 없다(가드가 물을 것이 없다). 하나라도 있으면 예전처럼 머리 배너만 세우고
 * 사용자가 입력을 옮겨 적은 뒤 직접 다시 불러온다.
 */
async function settleLowered(): Promise<void> {
  // 강등이 화면을 읽기 모드로 바꾸면 입력칸이 잠기며 포커스를 잃으므로, 편집 중인지는 기다리기 전에 본다
  const editingBefore = isEditingField();
  await new Promise((resolve) => setTimeout(resolve, LOWERED_SETTLE_MS));
  if (pendingRole.value || desiredRole.value === mountedRole.value) return;
  // `beforeunload` 처리기가 없는 쓰기 화면(전체 현황 인라인 편집 등)의 입력은 hasUnsavedWork 가 못 본다 — 쓰기가 취소됐거나 · 편집 중이거나 ·
  // 이 화면에서 값을 고친 적이 있으면 입력이 있는 것으로 보고 배너로 둔다(RVDS89-02).
  const writeCanceled = consumeLoweredWriteCancel();
  if (
    writeCanceled ||
    editingBefore ||
    isEditingField() ||
    fieldEdited ||
    hasUnsavedWork() ||
    hasPendingWrites()
  ) {
    roleReloadBlocked.value = true;
    return;
  }
  await reloadForRole(false);
}

watch(
  () => [workspace.value.id, desiredRole.value] as const,
  ([id, role], [prevId]) => {
    if (id !== prevId || !mountedRole.value) {
      authStore.applyPendingRole();
      mountedRole.value = workspace.value.role;
      roleReloadBlocked.value = false;
      return;
    }
    if (role === mountedRole.value) {
      // 강등(배너만 세우고 화면은 그대로) 뒤 마운트된 역할로 되올라가면 보류(`pendingRole`)가 남아 화면 역할은 낮은 채 잠겨 있다 —
      // 화면은 이미 그 역할로 그려져 있으므로 보류를 바로 반영해 잠금과 배너를 함께 푼다(리뷰 MED-3).
      authStore.applyPendingRole();
      roleReloadBlocked.value = false;
      return;
    }
    // 올라간 역할은 `pendingRole` 로 보류되고 내려간 역할만 바로 반영된다 — 보류가 없으면 강등이다(JR83-05): 다시 마운트 대신 배너만.
    // 단 저장하지 않은 입력도 진행 중 쓰기도 없으면 잃을 것이 없으니 곧바로 다시 불러온다(89차 B100 ⓒ R2).
    if (!pendingRole.value) {
      void settleLowered();
      return;
    }
    void reloadForRole(true);
  },
);

/** 강등으로 쓰기가 잠겼고 아직 다시 불러오지 않았다 — 드로어 · 모달이 층 안 머리 아래에 같은 안내를 둔다 */
provideRoleLowered({
  active: computed(() => roleReloadBlocked.value && !pendingRole.value),
  reload: () => void reloadForRole(false),
});

watch(
  currentPageId,
  (pageId) => {
    const related = getRelatedAccordionKeys(pageId);
    const missing = related.filter((key) => !openKeys.value.includes(key));
    if (missing.length > 0) {
      openKeys.value = [...openKeys.value, ...missing];
    }
    window.scrollTo({ top: 0 });
    // 메뉴가 사이드바 높이보다 길면 아래쪽 화면(운영 · 관리 그룹)의 현재 메뉴가 사이드바 스크롤 밖에 남아, 어디에 있는지
    // 보이지 않았다. 아코디언이 열린 뒤 현재 메뉴가 보이도록 사이드바만 스크롤한다(이미 보이면 움직이지 않는다).
    // `scrollIntoView` 는 창까지 스크롤해 위 `window.scrollTo` 를 끊을 수 있고, 바닥에 붙은 사용자 바 뒤에 메뉴를 둘 수 있어
    // (리뷰 rev-c M3 · L3) 사이드바 scrollTop 을 직접 계산한다.
    void nextTick(() => {
      const sidebar = sidebarEl.value;
      const active = sidebar?.querySelector<HTMLElement>(".nav-btn.active");
      if (!sidebar || !active) return;
      const footer = sidebar.querySelector<HTMLElement>(".sidebar-user");
      const footerHeight =
        footer && getComputedStyle(footer).position === "sticky"
          ? footer.offsetHeight
          : 0;
      const sidebarTop = sidebar.getBoundingClientRect().top;
      const rect = active.getBoundingClientRect();
      const top = rect.top - sidebarTop;
      const overflowBottom =
        rect.bottom - sidebarTop - (sidebar.clientHeight - footerHeight);
      if (top < 0) {
        sidebar.scrollTop += top - 8;
      } else if (overflowBottom > 0) {
        sidebar.scrollTop += overflowBottom + 8;
      }
    });
  },
  { immediate: true },
);
</script>

<template>
  <!-- data-ui-layout-root: 모달이 떠 있는 동안 overlay.ts 가 inert 를 거는 배경(74차 UXV74-06). AI 도우미 패널 · 우하단 버튼은
       `#app` 으로 옮겨 그려 이 밖이라 살아 있다(상단바 자리 버튼만 모달 배경막 뒤에서 함께 잠긴다). -->
  <div class="app" data-ui-layout-root>
    <a class="ui-skip-link" href="#main-content" @click="skipToMain">
      본문으로 건너뛰기
    </a>
    <div
      v-if="drawerOpen"
      class="drawer-backdrop"
      aria-hidden="true"
      @click="closeDrawer()"
    />
    <aside
      id="app-sidebar"
      ref="sidebarEl"
      class="sidebar"
      :class="{ 'is-drawer-open': drawerOpen }"
      :role="drawerOpen ? 'dialog' : undefined"
      :aria-modal="drawerOpen ? 'true' : undefined"
      :aria-label="drawerOpen ? '메뉴' : undefined"
      :tabindex="drawerOpen ? -1 : undefined"
    >
      <IconButton
        v-if="drawerOpen"
        class="drawer-close"
        icon="close"
        label="메뉴 닫기"
        variant="quiet"
        @click="closeDrawer()"
      />
      <div class="brand">
        <div class="logo">SH</div>
        <div>
          <!-- 화면당 h1 은 PageHero 하나(77차 UXS77-04) — 브랜드는 제목이 아니다 -->
          <div class="brand-name">셀렉허브</div>
          <p>조직적합성·직무적합성 설계 SaaS</p>
        </div>
      </div>

      <!--
        소속이 2개 이상이면 전환 셀렉트가 이름 자리(제목 줄)에 선다. 이름 줄 아래에 셀렉트를 두면 선택 값이 이름과 같은 글자라
        "밸류코퍼레이션"이 두 번 보였다(ui-refine-2 A-09). 셀렉트 값이 곧 현재 워크스페이스 이름이다.
      -->
      <div class="workspace">
        <select
          v-if="workspaces.length > 1"
          ref="workspaceSelect"
          v-model="selectedWorkspaceId"
          class="workspace-switch"
          :disabled="switching"
          :title="workspaceName"
          aria-label="워크스페이스 전환"
        >
          <option v-for="item in workspaces" :key="item.id" :value="item.id">
            {{ item.name }}
          </option>
        </select>
        <div v-else class="name" :title="workspaceName">
          {{ workspaceName }}
        </div>
        <div class="meta">{{ workspaceMeta }}</div>
      </div>

      <nav class="sidebar-nav" aria-label="주 메뉴">
        <div
          v-for="group in navigation"
          :key="group.key"
          :class="topGroupClass(group)"
        >
          <button
            type="button"
            class="nav-accordion top-accordion"
            :class="{ open: isOpen(group.key) }"
            :aria-expanded="isOpen(group.key)"
            @click="toggleAccordion(group.key)"
          >
            <span>
              <span v-if="group.icon" class="ico" aria-hidden="true">
                <UiIcon :name="group.icon" :size="16" />
              </span>
              {{ group.label }}
            </span>
            <UiIcon class="chev" name="chevron-down" :size="14" />
          </button>
          <div
            class="nav-submenu top-submenu"
            :class="{
              open: isOpen(group.key),
              'nested-submenu': group.children.some((child) =>
                isNavGroup(child),
              ),
            }"
          >
            <template
              v-for="child in group.children"
              :key="isNavGroup(child) ? child.key : child.pageId"
            >
              <template v-if="isNavGroup(child)">
                <button
                  type="button"
                  class="nav-accordion child-accordion"
                  :class="{ open: isOpen(child.key) }"
                  :aria-expanded="isOpen(child.key)"
                  @click="toggleAccordion(child.key)"
                >
                  <span>
                    <span v-if="child.icon" class="ico" aria-hidden="true">
                      <UiIcon :name="child.icon" :size="16" />
                    </span>
                    {{ child.label }}
                  </span>
                  <UiIcon class="chev" name="chevron-down" :size="14" />
                </button>
                <div
                  class="nav-submenu child-submenu"
                  :class="{ open: isOpen(child.key) }"
                >
                  <a
                    v-for="leaf in leafItems(child)"
                    :key="leaf.pageId"
                    :href="navHref(leaf.path)"
                    class="nav-btn sub-nav-btn"
                    :class="{ active: isActive(leaf) }"
                    :aria-current="isActive(leaf) ? 'page' : undefined"
                    @click="onNavClick($event, leaf.path)"
                  >
                    {{ leaf.label }}
                  </a>
                </div>
              </template>
              <a
                v-else
                :href="navHref(child.path)"
                class="nav-btn sub-nav-btn"
                :class="{ active: isActive(child) }"
                :aria-current="isActive(child) ? 'page' : undefined"
                @click="onNavClick($event, child.path)"
              >
                {{ child.label }}
              </a>
            </template>
          </div>
        </div>
      </nav>

      <div class="sidebar-user">
        <div class="who">
          <b>{{ info.name || "사용자" }}</b>
          <span :title="info.loginId || undefined">{{
            info.loginId || "-"
          }}</span>
        </div>
        <!-- 사용자 바는 한 줄 행이라 행 안 버튼 높이(36, `ui-sm`)로 둔다 — 폼 버튼 42 는 바를 67px 로 두껍게 했다(ui-craft 6). -->
        <button type="button" class="btn ghost ui-sm" @click="onLogout">
          로그아웃
        </button>
      </div>
    </aside>

    <main id="main-content" ref="mainEl" class="main" tabindex="-1">
      <header class="topbar">
        <button
          ref="menuButton"
          type="button"
          class="topbar-menu"
          aria-controls="app-sidebar"
          :aria-expanded="drawerOpen"
          aria-label="메뉴 열기"
          @click="openDrawer"
        >
          <svg
            width="20"
            height="20"
            viewBox="0 0 20 20"
            aria-hidden="true"
            focusable="false"
          >
            <path
              d="M3 5h14M3 10h14M3 15h14"
              stroke="currentColor"
              stroke-width="1.8"
              stroke-linecap="round"
            />
          </svg>
        </button>
        <div class="topbar-where" :class="{ 'is-root-only': showsRootOnly }">
          <nav
            class="breadcrumb"
            :class="{
              'has-trail': breadcrumbTrail.length > 0,
              'shows-near-group': showsNearGroup,
            }"
            aria-label="현재 위치"
          >
            <ol class="crumbs">
              <li class="crumb crumb-root">셀렉허브</li>
              <template v-if="breadcrumbTrail.length">
                <li
                  v-for="(label, index) in breadcrumbTrail"
                  :key="label"
                  class="crumb"
                  :class="{
                    'is-near-group': index === breadcrumbTrail.length - 1,
                  }"
                >
                  <span class="crumb-sep" aria-hidden="true">/</span>{{ label }}
                </li>
                <li class="crumb is-last" aria-current="page">
                  <span class="crumb-sep" aria-hidden="true">/</span
                  >{{ currentTitle }}
                </li>
              </template>
              <li v-else class="crumb">
                <span class="crumb-sep" aria-hidden="true">/</span>
                <span class="ui-sr-only">{{ currentTitle }}</span>
              </li>
            </ol>
          </nav>
          <div
            class="page-title"
            :class="{ 'ui-sr-only': breadcrumbTrail.length > 0 }"
            :aria-hidden="breadcrumbTrail.length > 0 ? 'true' : undefined"
          >
            {{ currentTitle }}
          </div>
        </div>
        <!--
          상단바 오른쪽 행동 칸(71차 UXG-02 · 05). 어느 화면에서든 여는 AI 도우미 — 넓은 폭(≥761)은 이 자리의 「AI 도우미」 버튼,
          좁은 폭은 우하단 버튼으로 `#app` 에 옮겨 그린다. RouterView 밖이라 화면을 옮겨도 대화가 이어진다.
        -->
        <div class="topbar-actions">
          <AiAssistant placement="topbar" />
        </div>
      </header>

      <section class="content">
        <!-- 화면 이동 알림(UXS77-03) — 처음부터 DOM 에 둔 라이브 영역. 눈에는 보이지 않는다 -->
        <div class="ui-sr-only" role="status">{{ routeAnnouncement }}</div>
        <!--
          `role="status"` 는 늘 있는 래퍼에 둔다(8차 리뷰 L4) — 라이브 영역이 내용과 함께 나타나면 화면 읽기 프로그램이 첫 알림을
          놓친다. 안쪽 안내만 `v-if` 로 붙였다 뗀다.
        -->
        <div role="status">
          <!-- 강등은 쓰기가 곧바로 잠기므로 "저장 뒤" 라고 하지 않는다(8차 S3-02). 모든 화면 위에 뜨는 띠라 primary 를 두지 않는다
               (페이지 primary 와 충돌, 73차 UXA73-50) — ghost sm + 되돌리는 동사. -->
          <Callout
            v-if="roleReloadBlocked"
            class="role-reload-callout"
            tone="warn"
            band
            icon
            :title="
              pendingRole
                ? '역할이 바뀌었습니다 — 저장 뒤 화면을 다시 불러오세요'
                : '역할이 바뀌었습니다 — 권한이 줄어 저장할 수 없습니다'
            "
          >
            <p v-if="pendingRole">
              새 권한은 화면을 다시 불러온 뒤 반영됩니다. 그 전까지는 이전
              권한으로 보입니다.
            </p>
            <p v-else>
              권한이 줄어 이 화면에서 저장할 수 없습니다. 화면에 남은 입력이
              있으면 옮겨 적은 뒤 화면을 다시 불러오세요.
            </p>
            <template #actions>
              <LoadingButton
                variant="ghost"
                size="sm"
                icon="refresh"
                @click="reloadForRole(false)"
              >
                화면 다시 불러오기
              </LoadingButton>
            </template>
          </Callout>
        </div>
        <RouterView :key="routerViewKey" />
      </section>
    </main>
  </div>
</template>

<style scoped>
/*
 * 프로토타입 `.app{grid-template-columns:292px …!important}` 가 1180px 이하에서 사이드바를 숨긴 뒤에도 남아
 * 981~1180px 구간에서 본문이 292px 첫 칸에 갇힌다(980px 이하 1열 규칙만 있음). 사이드바가 숨는 구간 전체를 1열로 맞춘다.
 */
@media (max-width: 1180px) {
  .app {
    grid-template-columns: minmax(0, 1fr) !important;
  }
}

/* 프로토타입 `.sidebar .brand p` · `.sidebar .workspace .meta` 가 #cbd5e1 로 덮여 흰 배경에서 거의 보이지 않는다. 메타 회색(--ui-text-3)으로 둔다. */
.sidebar .brand p,
.sidebar .workspace .meta {
  color: var(--ui-text-3) !important;
}

/* 로고 옆 부제는 로그인 · 오류 카드의 같은 로고 머리(main.scss, 12 · 500)와 한 모양으로 둔다 — 사이드바만 프로토타입 11px/800 이라
   글자 스케일(최소 12) 밖이었고, 로그인 → 앱으로 넘어오면 같은 문구의 굵기가 바뀌었다(ui-craft 14). */
.sidebar .brand p {
  font-size: 12px !important;
  font-weight: 500 !important;
}

/* 워크스페이스 카드는 사이드바 안 하위 상자라 반경 14(카드 22 · 하위 상자 14 · 컨트롤 12 스케일). 프로토타입 16 은 스케일 밖. */
.sidebar .workspace {
  border-radius: var(--ui-radius-subbox) !important;
}

/* 메뉴 버튼 · 그룹 머리 반경은 컨트롤 12. 프로토타입 `.sidebar button{border-radius:13px !important}` 가 스케일 밖 값이었다. */
.sidebar .nav-btn,
.sidebar .nav-accordion {
  border-radius: var(--ui-radius-control) !important;
}

/* 긴 워크스페이스 이름은 한 줄 말줄임으로 카드 높이를 고정한다(전체 이름은 title 로 보인다). */
.sidebar .workspace .name {
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}

/*
 * 아코디언: 열린 그룹 머리(파란 테두리 상자)와 하위 목록(흰 테두리 상자)이 겹쳐 2중 상자로 보였다. 머리는 상자 없이
 * 파란 글자로만 열림을 표시하고, 하위 목록은 상자 대신 왼쪽 안내선으로 들여 써 한 그룹으로 읽히게 한다.
 * 프로토타입 규칙(`.nav-accordion.open` · `.nav-submenu` 등)이 !important 라 같은 강도로 덮는다.
 */
.sidebar .nav-group {
  margin: 2px 0 !important;
}

.sidebar .nav-accordion {
  margin: 2px 0 !important;
  border-color: transparent !important;
  background: transparent !important;
  box-shadow: none !important;
}

.sidebar .nav-accordion:hover {
  background: var(--soft) !important;
}

.sidebar .nav-accordion.open {
  color: var(--ui-blue-700) !important;
}

/* 82차 UXS82-14: 아래 여백 10 → 6 — 조직 · 직무 적합성처럼 3단이 열리면 하위 목록 둘의 여백이 합쳐 사이드바 내용이 900px 화면보다 5px 길어 스크롤이 생겼다(8px 절약). */
.sidebar .nav-submenu {
  margin: 2px 0 6px 22px !important;
  padding: 2px 0 2px 10px !important;
  border: 0 !important;
  border-left: 2px solid var(--ui-line) !important;
  border-radius: 0 !important;
  background: transparent !important;
  box-shadow: none !important;
}

.sidebar .nav-submenu .sub-nav-btn {
  border-color: transparent !important;
  background: transparent !important;
  /* 78차 VB78-15: 긴 메뉴 이름(「입사지원서 자동 설계하기」)이 스크롤바로 좁아질 때 낱말 중간(`설계하 / 기`)에서 꺾이지 않고 낱말 단위로 꺾이며 마지막 줄에 한 글자가 남지 않게 한다. */
  overflow-wrap: break-word;
  text-wrap: pretty;
  word-break: keep-all;
}

.sidebar .nav-submenu .sub-nav-btn:hover {
  background: var(--soft) !important;
}

.sidebar .nav-submenu .sub-nav-btn.active {
  border-color: var(--ui-blue-600) !important;
  background: var(--ui-blue-600) !important;
  color: var(--ui-inverse-ink) !important;
}

/*
 * 현재 메뉴의 키보드 포커스 링(ui-refine-2 A-07). 드로어를 열면 첫 포커스가 현재 메뉴로 가는데(지금 있는 곳에서 시작), 전역 링
 * (바깥 파란 2px)이 파란 채움 바로 바깥에 붙어 두 겹 파랑이 돼 드로어에서 가장 무거운 요소가 됐다. 현재 메뉴만 채움 안쪽 흰 링으로
 * 그린다(흰색 / #2563eb 5.2:1 — 포커스 표시 대비 3:1 이상). 첫 포커스 위치는 그대로다.
 */
.sidebar .nav-btn.active:focus-visible {
  outline: var(--ui-focus-outline-inverse) !important;
  outline-offset: -4px;
}

/*
 * 90차 C90-36 a · b · c(BL90-01 I-2) — 프로토타입 `base.scss` 사이드바 블록(`:554` · `:559` · `:564` · `:570`)의 중요도(important) 선언이 이기던 값을 중요도 없이 여기에 둔다.
 * 원본 중요도를 걷으면 같은 파일의 앞쪽 일반 규칙(`.nav-accordion span:first-child{display:flex}` · `.nav-accordion .chev{font-size:14px}` ·
 * `.nav-accordion.top-accordion{color:#1e3a8a}`)이 이기는데, 화면 값은 지금 그대로여야 한다(첫 화면 diff 0). 아래는 그 일반 규칙들보다 높은 명시도
 * (`.sidebar` + scoped 속성)라 원본이 있어도 없어도 같은 값이 나온다. 값은 HEAD 계산값 그대로이고 토큰 정리는 별도다.
 */
/* a. 아이콘 칸 — 원본 `.nav-accordion .ico, .nav-btn .ico{display:inline-grid}`(flex 항목이라 계산값은 grid) */
.sidebar .nav-accordion .ico,
.sidebar .nav-btn .ico {
  display: inline-grid;
}

/* b. 펼침 화살표 — 원본 `.chev{font-size:12px}`. 줄높이는 글자 크기를 따라간다(18.6px) */
.sidebar .nav-accordion .chev {
  font-size: 12px;
}

/* c. 그룹 머리 글자색 — 원본 `.nav-accordion{color:#1e293b}`(열림은 아래 `.open` 규칙이 파랑). 하위 그룹 머리(`child-accordion`)도 같다 */
.sidebar .nav-accordion {
  color: #1e293b;
}

/*
 * d. 메뉴 버튼 크기 · 여백(92차 C92-36 · BL92-07 · 원본 `base.scss:580` `.nav-btn{min-height:38px !important; padding:8px 10px !important; margin:2px 0 !important}`) — 지금 값을 중요도 없이 둔다.
 * 원본 중요도를 걷으면 앞쪽 일반 규칙(`.nav-btn{padding:10px 12px;margin:2px 0}` · `.nav-submenu .sub-nav-btn{margin:0;padding-left:16px}` 명시도 0,2,0)이 이기는데
 * 화면 값은 지금 그대로여야 한다(상위 · 하위 메뉴 모두 8px 10px · 여백 2px 0). 아래는 `.sidebar` + scoped 속성으로 그 규칙들보다 높아 원본이 있어도 없어도 같은 값이 나온다.
 * 원본 중요도가 아직 이기므로 이 추가만으로는 화면이 바뀌지 않는다(ds-proto92 가 원본 중요도를 걷는다).
 */
.sidebar .nav-btn {
  min-height: 38px;
  margin: 2px 0;
  padding: 8px 10px;
}

/*
 * 열림 화살표(A-08 → 73차 UXA73-50). `▾` 글자 대신 공용 `UiIcon chevron-down`(14, select 펼침 표지와 같은 경로) — 장식이라 UiIcon 이
 * aria-hidden 을 준다. 열림 회전은 프로토타입 `.nav-accordion.open .chev{rotate(180deg)}` 그대로(svg 에도 걸린다). 색은 메타 회색
 * (--ui-text-3, 흰 바탕 4.8:1 — 아이콘 대비 3:1 이상), 열리면 파랑. 프로토타입 `.chev{font-size !important}` 는 svg 에 뜻이 없다.
 */
.sidebar .nav-accordion .chev {
  flex: none;
  margin-left: auto;
  color: var(--ui-text-3) !important;
}

.sidebar .nav-accordion.open .chev {
  color: var(--ui-blue-700) !important;
}

/* 하위 그룹 머리(맞춤 설계의 조직·직무 적합성)는 한 단계 작게 둔다. */
.sidebar .nav-accordion.child-accordion {
  margin: 2px 0 !important;
  padding: 8px 10px !important;
  font-size: 13px !important;
}

/*
 * 사이드바 메뉴 hover 전환 속도를 화면 카드와 같은 .16s 로 맞춘다.
 * 현재 메뉴(파란 채움)는 전환 없이 곧바로 옮긴다(71차 UXG-04) — 채움이 .16s 동안 서서히 빠지고 차는 사이 새 화면이 그려지느라
 * 늘어져, 이전 메뉴(채움)와 새 메뉴(테두리)가 둘 다 '현재' 처럼 보였다. hover 로 들어갈 때만 서서히, 나갈 때 · 현재 표시는 곧바로.
 */
.sidebar .nav-accordion,
.sidebar .nav-btn:hover:not(.active) {
  transition:
    background-color var(--ui-dur-fast) var(--ui-ease),
    color var(--ui-dur-fast) var(--ui-ease),
    transform var(--ui-dur-fast) var(--ui-ease);
}

.sidebar .nav-btn:not(:hover),
.sidebar .nav-btn.active {
  transition: none;
}

/*
 * 상단바: 한 줄 56px. 예전에는 breadcrumb(셀렉허브 / 제목) 아래에 26px/950 제목이 한 줄 더 있어 약 94px 흰 띠가 됐고, 바로 아래
 * 히어로가 다시 큰 제목을 가져 머리가 두 겹이었다. 페이지의 주인공은 히어로이므로 상단바는 "지금 어디인가"만 알리는 위치 줄로 둔다.
 * - breadcrumb 과 `.page-title` 은 둘 다 남긴다(라우팅 계약). breadcrumb 안의 제목 조각은 화면 읽기용으로만 남기고(바로 옆
 *   `.page-title` 과 같은 글자라 눈으로는 두 번 보였다), 눈에는 "셀렉허브 / {제목}" 한 줄로(72차 UXJ-01 — 사이드바 · 로그인 · 오류 카드와 같은 한글 표기) 읽히게 `.page-title` 을 이어 붙인다.
 * - 높이를 바꾸면 main.scss 의 `--ui-topbar-h` 도 같이 바꾼다(본문 sticky 패널이 이 값만큼 아래에 붙는다).
 * - 1180px 이하는 프로토타입이 static 으로 두고 760px 이하는 세로로 쌓는다 — 한 줄 배치는 그대로 두되 높이 고정만 푼다.
 */
.topbar {
  min-height: 56px;
  padding: 10px 24px !important;
  border-bottom: 1px solid var(--ui-line) !important;
  background: rgb(255 255 255 / 88%) !important;
  box-shadow: none !important;
}

/* 넓은 폭에서는 breadcrumb 글자 왼쪽 끝을 본문 블록 왼쪽 끝(`.content` 24 + `.page` 28 = 52px)에 맞춘다. 24px 이면 글자(x=316)와
   카드(x=344)의 세로 기준선이 둘로 갈렸다(ui-seam 4차, d4). 1180px 이하는 메뉴 버튼이 앞에 있어 아래 좁은 폭 규칙을 따른다. */
@media (min-width: 1181px) {
  .topbar {
    padding-inline: 52px !important;
  }
}

.topbar > .topbar-where {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 0 6px;
  min-width: 0;
}

.topbar .breadcrumb {
  min-width: 0;
  overflow: hidden;
  color: var(--ui-text-3);
  font-size: 13px;
  font-weight: 500;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* 78차 UXA78-06 · 86차 R86-visual-08: 위치 줄 = 「셀렉허브 / 그룹 / 하위 그룹 / 화면 이름」. 마지막 칸(화면 이름)만 한 단 진하게. */
/* 목록(`ol`)이지만 한 줄 글처럼 — 칸은 인라인이라 말줄임이 먹는다. 구분자 「/」 는 장식(낭독 제외) */
.topbar .breadcrumb .crumbs {
  margin: 0;
  padding: 0;
  list-style: none;
}

.topbar .breadcrumb .crumb {
  display: inline;
}

.topbar .breadcrumb .crumb-sep {
  margin: 0 6px;
}

.topbar .breadcrumb .crumb.is-last {
  color: var(--ui-text-2);
  font-weight: 700;
}

/* 좁은 폭은 화면 이름 한 칸만(상단바 높이 고정). 그룹 경로는 사이드바 드로어에 있다 */
@media (max-width: 620px) {
  .topbar .breadcrumb.has-trail .crumb:not(.is-last) {
    display: none;
  }

  .topbar .breadcrumb.has-trail .crumb.is-last .crumb-sep {
    display: none;
  }

  /* 89차 C89-06: 화면 이름이 바로 아래 h1 과 같은 화면(`meta.heroIsTitle`) · 부모가 있는 하위 화면(`meta.parentTitle`)은 가장 가까운 그룹 · 부모 한 칸이
     눈에 보이는 한 줄이다. 화면 이름 칸은 화면 읽기용으로 남긴다(`aria-current`). */
  .topbar .breadcrumb.has-trail.shows-near-group .crumb.is-near-group {
    display: inline;
  }

  .topbar
    .breadcrumb.has-trail.shows-near-group
    .crumb.is-near-group
    .crumb-sep {
    display: none;
  }

  .topbar .breadcrumb.has-trail.shows-near-group .crumb.is-last {
    position: absolute;
    width: 1px;
    height: 1px;
    overflow: hidden;
    clip-path: inset(50%);
    white-space: nowrap;
  }
}

.topbar .page-title {
  margin: 0 !important;
  color: var(--ink);
  font-size: 16px !important;
  font-weight: 800 !important;
  line-height: 1.4;
  letter-spacing: -0.02em !important;
}

@media (min-width: 761px) {
  .topbar {
    height: 56px;
  }
}

/* 사용자 영역을 사이드바 바닥에 붙이고, 메뉴가 길 때도 마지막 그룹과 18px 이상 띄운다(데스크톱 사이드바 · 좁은 폭 드로어 공통). */
@media (min-width: 1181px) {
  .sidebar {
    display: flex;
    flex-direction: column;
  }
}

/*
   * 메뉴를 많이 펼치면 사이드바가 스크롤되어 사용자 영역(이름 · 로그아웃)이 화면 밖으로 밀려 잘렸다(900px 높이 화면에서 운영 ·
   * 맞춤 설계 그룹을 펼치면 항상). 사이드바 바닥에 붙은 불투명 바(위 1px 구분선)로 두어 늘 보이게 한다.
   * - 반투명 흰 그림자로 뒤 메뉴를 덮던 방식은 마지막 메뉴가 비쳐 글자가 겹친 듯 보였다(리드 캡처) — 덮개 없이 불투명 배경만 쓴다.
   * - 바가 사이드바 가장자리까지 닿게 좌우 패딩(프로토타입 실측 14px)은 음수 여백으로 걷고, 아래 패딩은 0 으로 둔다 — 스크롤
   *   컨테이너의 아래 패딩은 마지막 요소의 음수 여백으로 걷히지 않아, 끝까지 스크롤하면 바 밑에 18px 틈이 생겼다.
   * - 바는 흐름 안의 마지막 요소라 메뉴 목록 끝에 자기 높이만큼 자리를 차지한다 — 끝까지 스크롤하면 마지막 메뉴가 바 위에 온전히 보인다.
   * - 키보드 Tab 으로 아래쪽 메뉴에 포커스가 가면 브라우저가 스크롤해 주는데, 바 뒤에 가려지지 않게 바 높이(약 64px)+여유만큼
   *   scroll-padding 을 둔다(리뷰 rev-c M3).
   */
/* 1180px 이하에서는 사이드바가 숨어 있으므로 아래 규칙은 데스크톱 사이드바와 열린 드로어에만 보인다. */
.sidebar {
  padding-bottom: 0 !important;
  scroll-padding-bottom: 80px;
}

/* 78차 UXA78-07 · VA78-26: 긴 이메일은 주소 중간에서 꺾이지 않고 이름처럼 한 줄 말줄임(전체 값은 `title`) */
.sidebar .sidebar-user .who span {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.sidebar > .sidebar-user {
  position: sticky;
  bottom: 0;
  z-index: 1;
  margin: auto -14px 0 !important;
  padding: 12px 14px !important;
  border: 0 !important;
  border-top: 1px solid var(--ui-line) !important;
  border-radius: 0 !important;
  background: var(--ui-surface) !important;
  box-shadow: none !important;
}

/* 메뉴 묶음 `nav`(77차 UXS77-04) — 그룹 사이 위아래 여백 2 + 2 가 겹쳐 합쳐지지 않게 flex 열로 둔다. 예전에도 사이드바(≥1181)와 열린 드로어(≤1180,
   `.sidebar.is-drawer-open` 도 flex 열)의 그룹이 flex 항목이라 4px 였다 — 간격은 그대로다(fix1 L5 확인). */
.sidebar-nav {
  display: flex;
  flex-direction: column;
}

/* 건너뛰기 링크의 포커스 대상(`main`)은 조작 요소가 아닌 랜드마크 — UA 기본 포커스 링을 그리지 않는다(fix1 L4) */
.main:focus {
  outline: none;
}

.sidebar-nav > .nav-group:last-child {
  margin-bottom: 18px !important;
}

/* 브랜드 이름 — 실제로 이기던 프로토타입 규칙(`base.scss:956` `.brand h1{color:#0f172a; font-size:20px; font-weight:950 !important}`)과 같은 모양
   (950 은 굵기 스케일 밖이라 900), 제목 요소가 아니다 */
.sidebar .brand-name {
  margin: 0;
  color: var(--ui-text);
  font-size: 20px;
  font-weight: 900;
  letter-spacing: -0.06em;
}

/* 메뉴 항목이 링크(`a`)가 됐다 — 버튼 리셋을 전제한 프로토타입 `.nav-btn` 에 링크 기본 모양만 걷는다 */
.sidebar a.nav-btn {
  text-decoration: none;
}

/* 역할 변경 경고(warn 안내)는 페이지 위 카드급 띠(`ui-band`, 가로 여백 22 · 반경 18)로 두고, 아래 화면과는 최상위 블록 간격 18 을 둔다. */
.role-reload-callout {
  margin-bottom: 18px;
}

/*
 * 좁은 폭 메뉴 버튼 · 드로어(ds-audit C1). 1180px 초과에서는 사이드바가 늘 보이므로 메뉴 버튼을 숨긴다.
 * - 메뉴 버튼은 44×44 터치 대상, 상단바 왼쪽. 760px 이하에서 프로토타입이 상단바를 세로로 쌓던 것은 한 줄로 되돌린다(버튼 · 위치 줄).
 * - 드로어는 사이드바 그대로를 왼쪽에 고정해 띄운다(폭 306 또는 화면의 86%). 프로토타입 980px 규칙(position:relative · height:auto
 *   !important)을 이기도록 `!important` 를 붙인다. 모달(z 9999)보다 아래라 드로어에서 연 확인창이 위에 뜬다.
 */
.topbar-menu {
  display: none;
}

@media (max-width: 1180px) {
  /* 상단바를 좁은 폭에서도 화면 위에 붙인다(리뷰 B9) — 프로토타입 `.topbar{position:static}` 때문에 긴 화면 아래에서 메뉴 버튼에
     닿으려면 맨 위까지 올라가야 했다. 높이 56px 은 main.scss `--ui-topbar-h` 와 같다(본문 sticky 패널이 그 아래에 붙는다).
     드로어(60) · 모달(9999)보다 아래. */
  .topbar {
    position: sticky !important;
    top: 0;
    z-index: var(--ui-z-topbar) !important;
    flex-direction: row !important;
    align-items: center !important;
    justify-content: flex-start !important;
    gap: 8px !important;
    height: 56px;
    padding: 6px 14px !important;
  }

  /* 긴 화면 이름은 한 줄 말줄임(상단바 높이 고정). 전체 이름은 화면 히어로 제목에 있다. */
  .topbar > .topbar-where {
    flex: 1 1 auto;
    flex-wrap: nowrap;
  }

  .topbar .page-title {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .topbar-menu {
    display: grid;
    flex: none;
    place-items: center;
    width: 44px;
    height: 44px;
    margin-left: -6px;
    padding: 0;
    border: 0;
    border-radius: var(--ui-radius-control);
    background: transparent;
    color: var(--ui-text);
    cursor: pointer;
  }

  .topbar-menu:hover {
    background: var(--ui-surface-2);
  }

  .sidebar.is-drawer-open {
    position: fixed !important;
    inset: 0 auto 0 0;
    z-index: var(--ui-z-drawer);
    display: flex;
    flex-direction: column;
    width: min(306px, 86vw);
    height: 100vh !important;
    height: 100dvh !important;
    max-height: none !important;
    overflow-y: auto;
    overscroll-behavior: contain;
    box-shadow: var(--ui-shadow-3);
  }

  .sidebar.is-drawer-open:focus {
    outline: none;
  }

  /* 로고 줄 오른쪽에 닫기 버튼 자리를 비운다. */
  .sidebar.is-drawer-open > .brand {
    padding-right: 44px !important;
  }

  .drawer-backdrop {
    position: fixed;
    inset: 0;
    z-index: calc(var(--ui-z-drawer) - 1);
    background: var(--ui-scrim);
  }

  /* 드로어 닫기 — 표준 `IconButton variant="quiet"`(모양 · hover · 포커스는 부품). 여기서는 자리와 터치 대상 44(리뷰 RV-FA-13 —
     전역 `is-md` 36 을 같은 `!important` 로 덮는다. 390 드로어의 다른 터치 대상 42~44 와 맞춘다). */
  .drawer-close {
    position: absolute;
    top: 10px;
    right: 8px;
    z-index: 2;
    width: 44px !important;
    min-width: 44px !important;
    height: 44px !important;
    min-height: 44px !important;
  }
}

/* 620px 이하에서는 "셀렉허브 /" 접두까지 두면 긴 화면 이름이 두 줄로 꺾였다(visual-audit V1). 접두는 화면 읽기용으로만 남기고
   눈에는 [메뉴 버튼 · 화면 이름] 한 줄만 보인다. */
@media (max-width: 620px) {
  /* 그룹 경로가 있는 화면(78차)은 화면 이름 대신 가장 가까운 그룹 한 칸이 눈에 보이는 한 줄이다 — 위 `has-trail` 규칙 */
  .topbar .breadcrumb:not(.has-trail) {
    position: absolute;
    width: 1px;
    height: 1px;
    overflow: hidden;
    clip-path: inset(50%);
    white-space: nowrap;
  }

  /* 그룹 경로 없는 시작 루트 화면의 제목이 h1 과 같으면(`is-root-only`, 90차 RG90-02) 화면 이름 대신 루트 「셀렉허브」 만 눈에 보인다. 이름 칸 ·
     `.page-title` 은 화면 읽기용으로 남긴다. */
  .topbar .topbar-where.is-root-only .breadcrumb:not(.has-trail) {
    position: static;
    width: auto;
    height: auto;
    clip-path: none;
  }

  .topbar .topbar-where.is-root-only .breadcrumb .crumb:not(.crumb-root),
  .topbar .topbar-where.is-root-only .page-title {
    position: absolute;
    width: 1px;
    height: 1px;
    overflow: hidden;
    clip-path: inset(50%);
    white-space: nowrap;
  }
}
</style>
